import { motion } from 'framer-motion';

interface Props {
  score: number;
  label?: string;
  size?: number;
  thickness?: number;
}

const scoreColor = (score: number): { stroke: string; text: string; band: string } => {
  if (score >= 75) return { stroke: 'stroke-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', band: 'Healthy' };
  if (score >= 55) return { stroke: 'stroke-teal-500', text: 'text-teal-600 dark:text-teal-400', band: 'Stable' };
  if (score >= 35) return { stroke: 'stroke-amber-500', text: 'text-amber-600 dark:text-amber-400', band: 'Watch' };
  return { stroke: 'stroke-rose-500', text: 'text-rose-600 dark:text-rose-400', band: 'Critical' };
};

export default function HealthScoreGauge({ score, label = 'Health Score', size = 132, thickness = 12 }: Props) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, score));
  const offset = circumference - (clamped / 100) * circumference;
  const color = scoreColor(clamped);

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={thickness}
            fill="none"
            className="stroke-slate-200 dark:stroke-slate-700"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            strokeWidth={thickness}
            fill="none"
            strokeLinecap="round"
            className={color.stroke}
            initial={{ strokeDasharray: circumference, strokeDashoffset: circumference }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold tabular-nums ${color.text}`}>{Math.round(clamped)}</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide ${color.text}`}>{color.band}</span>
        </div>
      </div>
      <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
    </div>
  );
}
